/**
 * The cost line sysadmins see under an AI answer (spec 2026-10-07-ki-nutzung-kosten §4), shared by the
 * Vue-2 admin (components/helper/KiKostenZeile.vue) and the canvas (components/canvas/KiTextPanel.vue).
 * Input: the flat fields kostenAufrufe, kostenTokens, kostenMikroUsd, kostenMikroEur, kostenModelle; the
 * server sends them to sysadmins only. Plain JS for the admin build; types in kiKosten.d.ts.
 */

// Readable model names; unknown ids are shown as they are.
const MODEL_NAMES = {
  'claude-opus-5-5': 'Opus 5.5',
  'claude-sonnet-5-5': 'Sonnet 5.5',
  'claude-haiku-4-5': 'Haiku 4.5',
};

const missing = (v) => v === null || v === undefined;

/** The cost fields of an answer as one object, or null when it has none (no sysadmin, no paid call). */
export function aiCostFrom(r) {
  if (!r || !r.kostenAufrufe) return null;
  return {
    kostenAufrufe: r.kostenAufrufe,
    kostenTokens: r.kostenTokens || 0,
    kostenMikroUsd: missing(r.kostenMikroUsd) ? null : r.kostenMikroUsd,
    kostenMikroEur: missing(r.kostenMikroEur) ? null : r.kostenMikroEur,
    kostenModelle: r.kostenModelle ? r.kostenModelle.slice() : [],
  };
}

/** Two answers of one wish (e.g. a repair round) as one cost; one unknown price makes the sum unknown. */
export function aiCostPlus(a, b) {
  const x = aiCostFrom(a);
  const y = aiCostFrom(b);
  if (!x || !y) return x || y;
  const plus = (p, q) => (p === null || q === null ? null : p + q);
  return {
    kostenAufrufe: x.kostenAufrufe + y.kostenAufrufe,
    kostenTokens: x.kostenTokens + y.kostenTokens,
    kostenMikroUsd: plus(x.kostenMikroUsd, y.kostenMikroUsd),
    kostenMikroEur: plus(x.kostenMikroEur, y.kostenMikroEur),
    kostenModelle: x.kostenModelle.concat(y.kostenModelle.filter((m) => x.kostenModelle.indexOf(m) < 0)),
  };
}

// Below 1 € two significant digits ("0,0046"), else cents ("1,23").
function euro(micro) {
  const e = micro / 1000000;
  return e.toLocaleString('de-DE', e < 1 ? { maximumSignificantDigits: 2 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function modelName(m) {
  return Object.prototype.hasOwnProperty.call(MODEL_NAMES, m) ? MODEL_NAMES[m] : m;
}

/** "Opus 5.5 · 2 Aufrufe · 18.400 Tokens · ≈ 0,62 €"; '' without costs. */
export function aiCostText(r) {
  const k = aiCostFrom(r);
  if (!k) return '';
  const parts = [];
  if (k.kostenModelle.length) parts.push(k.kostenModelle.map(modelName).join(' + '));
  parts.push(k.kostenAufrufe === 1 ? '1 Aufruf' : `${k.kostenAufrufe} Aufrufe`);
  parts.push(`${k.kostenTokens.toLocaleString('de-DE')} Tokens`);
  parts.push(k.kostenMikroUsd === null || k.kostenMikroEur === null ? 'Kosten unbekannt (Modell ohne Preis)' : `≈ ${euro(k.kostenMikroEur)} €`);
  return parts.join(' · ');
}
